'use client';

import { useSession } from '@shared/auth';
import { PLAY_ROUTE, useHoverRoutePrefetch } from '@shared/hooks';
import { twMerge } from '@shared/themes';
import Image from 'next/image';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import { BaseCard } from '../BaseCard/BaseCard';
import { HeartButtonWrapper } from '../HeartButtonWrapper/HeartButtonWrapper';
import { InfoButton } from '../InfoButton/InfoButton';

type TrendGameCardProps = ComponentProps<'div'> & {
  id: string;
  rank: number;
  image: string;
  isFavourited?: boolean;
  countFavourites?: number;
  href: string;
  priority?: boolean;
  gameName: string;
  gameProvider: string;
  position?: number;
  jackpotLabelText?: string;
  infoButtonHref?: string;
};

function Header({
  countFavourites,
  id,
  title,
  isFavourited,
  provider,
}: {
  countFavourites: number | undefined;
  id: string;
  title: string;
  isFavourited: boolean;
  provider: string;
}) {
  return (
    <BaseCard.Header className="justify-end gap-0 items-center transition-opacity md:opacity-0 group-hover:md:opacity-100">
      <FavouriteCounter count={countFavourites} />
      <HeartButtonWrapper
        id={id}
        name={title}
        namespace="casino.favourite"
        isFavourited={isFavourited}
        data-testid="trend-game-card-heart-button"
        data-track-name="Favourite"
        provider={provider}
      />
    </BaseCard.Header>
  );
}

function Content({ image, priority, title }: { image: string; priority: boolean | undefined; title: string }) {
  return (
    <BaseCard.Content className="mx-auto h-29.25 flex items-center justify-center">
      <Image src={image} priority={priority} unoptimized={true} alt={title} width={112} height={86} className="w-full" />
    </BaseCard.Content>
  );
}

function Footer({ title }: { title: string }) {
  return (
    <BaseCard.Footer className="flex flex-col items-start gap-1">
      <h3 className="paragraph-explanatory-strong text-foreground truncate w-full overflow-hidden text-ellipsis" data-testid="trend-game-card-title">
        {title}
      </h3>
    </BaseCard.Footer>
  );
}

function FavouriteCounter({ count }: { count: number | undefined }) {
  return count ? (
    <p className="paragraph-s" data-testid="trend-game-card-counter">
      {count}
    </p>
  ) : null;
}

function Rank({ rank }: { rank: number }) {
  const rankNumbers = rank.toString().split('');
  const classes = {
    container: 'absolute bottom-0 -left-6.75 flex',
    number: 'w-10 h-14 text-center',
    // non-standard Chroma font sizes
    numberText: 'text-[64px] font-bold text-accent-red leading-none',
  };
  return (
    <div className={classes.container}>
      {rankNumbers.map((num, index) => (
        <div className={twMerge(classes.number, index === 1 ? 'overflow-hidden' : '')} key={rank + index}>
          <p className={classes.numberText}>{num}</p>
        </div>
      ))}
    </div>
  );
}

function TrendGameCard({
  image,
  isFavourited = false,
  countFavourites,
  className,
  href,
  id,
  priority,
  gameName,
  gameProvider,
  position = 0,
  infoButtonHref,
  rank,
  ...props
}: TrendGameCardProps) {
  const classes = {
    container: 'w-41 group',
    root: 'relative w-34 h-45 ml-auto overflow-visible',
    card: 'relative h-full group-hover:scale-[1.08] transition-all duration-300 z-10 rounded-md overflow-hidden',
    link: 'bg-trending-card-background/90 dark:bg-trending-card-background/80 h-full p-3 absolute inset-0 backdrop-blur-[12px] flex flex-col justify-between',
    info: 'absolute bottom-3 right-3 transition-opacity md:opacity-0 group-hover:md:opacity-100',
  };
  const {
    session: { status },
  } = useSession();
  const routePrefetch = useHoverRoutePrefetch({ href, isAuthenticated: status === 'authenticated', authPathMatch: PLAY_ROUTE });

  return (
    <div className={twMerge(classes.container, className)} {...props}>
      <BaseCard.Root
        className={classes.root}
        data-track-container="Game"
        data-track-game-id={id}
        data-track-game-name={gameName}
        data-track-game-provider={gameProvider}
        data-track-game-position={position.toString()}
        data-track-game-favourited={isFavourited.toString()}
        data-track-game-callout-show={false.toString()}
      >
        <div className={classes.card}>
          <Link href={href} className={classes.link} {...routePrefetch}>
            <Header countFavourites={countFavourites} id={id} title={gameName} isFavourited={isFavourited} provider={gameProvider} />
            <Content image={image} priority={priority} title={gameName} />
            <Footer title={gameProvider} />
          </Link>
          <InfoButton href={infoButtonHref ?? href.substring(0, href.lastIndexOf('/'))} data-testid="trend-game-card-info-button" className={classes.info} data-track-name="Info" />
        </div>
        <Rank rank={rank} />
      </BaseCard.Root>
    </div>
  );
}

export { Content, FavouriteCounter, Footer, Header, Rank, TrendGameCard, type TrendGameCardProps };
